import { FC } from 'react';
import cn from 'classnames';
import styled from 'styled-components';

import { VfoFileIcon } from '@/features/vfo/components/VfoFileIcon';
import { ApiDocumentResponseV2 } from '@/shared/api';
import { COLORS } from '@/shared/constants/colors';
import { Icon } from '@/shared/uikit/Icon';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';

type FilesTabProps = {
  files: ApiDocumentResponseV2[];
  isCaseDeleted?: boolean;
};

export const FilesTab: FC<FilesTabProps> = ({ files, isCaseDeleted }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'vfoChatInfo' });

  if (isCaseDeleted) {
    return (
      <EmptyContainer>
        <Icon name="empty" size="s44" style={{ color: COLORS.systemDark100 }} />
        <b>{t('caseDeletedNoFiles')}</b>
      </EmptyContainer>
    );
  }

  if (files.length === 0) {
    return (
      <EmptyContainer>
        <Icon name="file-text" size="s44" style={{ color: COLORS.systemDark100 }} />
        <b>{t('noFiles')}</b>
      </EmptyContainer>
    );
  }

  return (
    <Container>
      {files.map((file) => (
        <FileItem key={file.title}>
          <FileIcon className={cn({ isExternal: file.source === 'EXTERNAL' })}>
            <VfoFileIcon file={file} size="s20" />
          </FileIcon>
          <Text>{file.title}</Text>
        </FileItem>
      ))}
    </Container>
  );
};

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  color: ${COLORS.systemDark200};
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  overflow: auto;
  max-height: calc(100vh - 420px);
`;

const FileItem = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  height: 64px;
  font-size: 12px;
  overflow: hidden;
  flex-shrink: 0;
`;

const FileIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  width: 28px;
  background: ${COLORS.systemDark200};
  border-radius: 4px;
  color: ${COLORS.white};
  flex-shrink: 0;

  &.isExternal {
    background: ${COLORS.systemSky300};
  }
`;

const Text = styled.span`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
